// Modules
import { useCallback, useMemo } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import _ from 'lodash';

// Services
import Call from 'services/data/call';
import Email from 'services/data/email';
import MonitoringAlert from 'services/data/monitoring-alert';

// Actions
import { alerts } from 'modules/alerts/actions';


const SERVICES = { call: Call, email: Email, monitoringalert: MonitoringAlert };

const LABELS = { call: 'Call', email: 'Email', monitoringalert: 'Monitoring alert' };

/**
 * Every cached list of triage items.
 *
 * The queue keeps one at `['triage']` (store/actions.js); a ticket's timeline keeps
 * its own at `['ticket', id, 'triage']` (routes/ticket/hooks/useTicket.js). The same
 * item can be on screen in both at once, so a patch has to reach both — matching on
 * the key segment is what saves this from having to know about ticket caches.
 */
const isTriageList = query => _.includes(query.queryKey, 'triage');

/**
 * Relations the API hands back as foreign keys — `claimedBy: 42` where the list
 * carries the loaded person (the `load` list in the api's
 * portal/modelUtils/triage/getItems.js).
 *
 * Writing an id over a loaded record blanks the row out rather than updating it:
 * `_.isEmpty(42)` is true, so a call that was just claimed reads as unclaimed, and
 * `Person.fullName(42)` renders nothing. So a patch only carries a relation it
 * actually has the record for. Null and undefined still apply — those are a clear
 * and a rollback, not a missing load.
 */
const RELATION_KEYS = [
  'fromPerson', 'claimedBy', 'userOnCall', 'lastUserOnCall', 'transferringTo',
  'resolvedBy', 'company', 'ticket', 'recording', 'replyUser'
];

const isForeignKey = (value, key) => _.includes(RELATION_KEYS, key) && !_.isNil(value) && !_.isObject(value);

const loadedFields = patch => _.omitBy(patch, isForeignKey);

function patchItems(items, patch, type) {
  return _.map(items, item => (item.id === patch.id && item.type === type ? { ...item, ...patch } : item));
}

/**
 * Patch one item, in place, in every list that holds it.
 *
 * In place is the point: replacing a list would reorder it, and holding reorders
 * back until the queue goes quiet is what the gate in store/actions.js exists to
 * do. The row the user just acted on updates where it already sits.
 */
export function patchTriageItem(queryClient, patch, type) {
  const fields = loadedFields(patch);

  queryClient.setQueriesData({ predicate: isTriageList }, current => {
    if (_.isArray(current)) return patchItems(current, fields, type);

    // The queue caches `{ version, items }` — keep the envelope so the version
    // still describes the list it came with.
    if (_.isArray(current?.items)) return { ...current, items: patchItems(current.items, fields, type) };

    return current;
  });
}

function findTriageItem(queryClient, id, type) {
  return _(queryClient.getQueriesData({ predicate: isTriageList }))
    .flatMap(([, data]) => (_.isArray(data) ? data : _.get(data, 'items')))
    .find(item => item && item.id === id && item.type === type);
}

/**
 * One action: optimistic patch on the way out, server record merged on the way
 * back, previous values restored if it fails.
 *
 * @param request     Fires the request. Resolves to the updated record.
 * @param optimistic  Builds the patch to apply before the request goes out.
 * @param onFailure   User-facing handling for a rejected request.
 * @param swallow     Resolve rather than reject on failure, for the callers that
 *                    fire and forget — `onFailure` has already told the user, so
 *                    rejecting would only produce an unhandled rejection. `update`
 *                    leaves this off because its callers chain on the result.
 */
function useTriageAction({ queryClient, type, request, optimistic, onFailure, swallow }) {
  const { mutateAsync } = useMutation(request, {
    onMutate: variables => {
      const patch = optimistic(variables);
      const previous = findTriageItem(queryClient, patch.id, type);

      patchTriageItem(queryClient, patch, type);

      // Only the fields the patch touched, so a rollback cannot clobber an
      // unrelated update that landed while the request was in flight. A field the
      // item did not have maps to undefined, which puts it back to not having it.
      return { rollback: previous && _.mapValues(patch, (value, key) => previous[key]) };
    },

    // The record is authoritative for everything it fully describes, which
    // corrects any optimistic guess within the round trip instead of leaving it
    // to stand until the next version lands.
    onSuccess: record => {
      if (_.isObject(record) && record.id) patchTriageItem(queryClient, record, type);
    },

    onError: (error, variables, context) => {
      if (context?.rollback) patchTriageItem(queryClient, context.rollback, type);
      if (onFailure) onFailure(error);
    }
  });

  return useCallback(
    variables => (swallow ? mutateAsync(variables).catch(_.noop) : mutateAsync(variables)),
    [mutateAsync, swallow]
  );
}

/**
 * The write half of the triage queue: every action that changes a triage item,
 * with the cache patching that keeps the change visible.
 *
 * It exists because the server no longer pushes the updated queue — it publishes a
 * version, and the client refetches on a gate that waits for the user to stop
 * interacting (store/actions.js). Anything that does not patch the cache itself is
 * therefore invisible for as long as the user keeps working, which is exactly when
 * they are looking. Reading the cache through `useQueryClient` rather than taking a
 * writer as a prop is what lets a component several levels down act on an item
 * without the queue having to hand it the means.
 */
export function useTriageMutation({ type, currentUser }) {
  const queryClient = useQueryClient();

  const update = useTriageAction({
    queryClient,
    type,
    request: patch => SERVICES[type].update(patch),
    optimistic: patch => patch
  });

  const claim = useTriageAction({
    queryClient,
    type,
    swallow: true,
    request: item => SERVICES[type].claim(item.id).then(response => response.data),
    optimistic: item => ({ id: item.id, claimedBy: currentUser, status: 'Active' }),
    onFailure: error => {
      if (_.get(error, 'response.status') === 404) {
        alerts.warning({ title: `${LABELS[type]} has already been claimed` });
        return;
      }

      alerts.catchError(error);
    }
  });

  /**
   * Answering is the one action whose outcome the client cannot read off its own
   * request, so it is worth being explicit about what the patch claims: the call is
   * being transferred to whoever answered, which is us. That renders as
   * "Answering: <name>" (see the `initialAnswer` branch in triage-items/call.js) and
   * drops the answer option, which is the whole of what the user needs to see.
   */
  const answer = useTriageAction({
    queryClient,
    type,
    swallow: true,
    request: call => Call.answer(call.id).then(response => response.data),
    optimistic: call => ({
      id: call.id,
      callStatus: 'Transferring',
      claimedBy: currentUser,
      transferringTo: currentUser
    }),
    onFailure: alerts.catchError
  });

  const transfer = useTriageAction({
    queryClient,
    type,
    swallow: true,
    request: ({ call, user }) => Call.transfer(call.id, user.id).then(response => response.data),
    optimistic: ({ call, user }) => ({ id: call.id, callStatus: 'Transferring', transferringTo: user }),
    onFailure: alerts.catchError
  });

  return useMemo(
    () => (type === 'call' ? { update, claim, answer, transfer } : { update, claim }),
    [type, update, claim, answer, transfer]
  );
}
